import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import DashboardLayout from "@/components/DashboardLayout";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Check, Star, Crown, Sprout, AlertCircle, Receipt } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = { live_queue: "mod_live_queue", thermal_printing: "mod_thermal_printing", analytics: "mod_analytics", store: "mod_store" };
const PLAN_ICON = { starter: Sprout, professional: Star, enterprise: Crown };
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function Billing() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/payments/history").then((r) => setHistory(r.data));
  }, []);

  const handlers = (plan_id) => ({
    createOrder: () => api.post("/payments/paypal/create-order", { plan_id, cycle: "yearly" }).then((r) => r.data.order_id),
    onApprove: (data) => api.post(`/payments/paypal/capture/${data.orderID}`).then(({ data: res }) => {
      setUser(res.user);
      toast.success(t("plan_activated"));
      navigate("/dashboard");
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || t("payment_failed"))),
    onError: () => toast.error(t("payment_failed")),
  });

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl space-y-6" data-testid="billing-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("billing_title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("billing_sub")}</p>
          <p className="mt-2 text-sm">{t("current_plan")}: <span className="rounded-full bg-olive-light px-2.5 py-1 font-semibold text-olive">{t(`plan_${user?.plan}`)}</span></p>
        </div>

        {!CLIENT_ID && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="paypal-missing">
            <AlertCircle className="h-4 w-4" /> {t("paypal_missing")}
          </div>
        )}

        {plans.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-3">
            {plans.map((plan) => {
              const Icon = PLAN_ICON[plan.plan_id] || Star;
              const isCurrent = user?.plan === plan.plan_id;
              const mods = Object.entries(plan.modules || {}).filter(([, v]) => v).map(([k]) => k);
              return (
                <div key={plan.plan_id} data-testid={`billing-plan-${plan.plan_id}`}
                  className={`relative flex flex-col rounded-2xl border bg-card p-7 ${plan.plan_id === "professional" ? "border-primary shadow-lg" : "border-border"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-olive-light text-olive"><Icon className="h-5 w-5" /></div>
                    {isCurrent && <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">{t("current_plan")}</span>}
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-bold">{t(`plan_${plan.plan_id}`)}</h3>
                  <p className="text-sm text-muted-foreground">{t(`plan_${plan.plan_id}_d`)}</p>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-heading text-4xl font-extrabold">€{plan.yearly_price}</span>
                    <span className="mb-1 text-sm text-muted-foreground">{t("per_year")}</span>
                  </div>
                  <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("includes")}</p>
                  <ul className="mt-2 flex-1 space-y-2">
                    {mods.map((m) => (
                      <li key={m} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 shrink-0 text-primary" /> {t(MODULE_LABELS[m])}</li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    {isCurrent ? (
                      <div className="rounded-lg border border-olive-border bg-olive-light py-2.5 text-center text-sm font-semibold text-olive">{t("youre_on_this")}</div>
                    ) : CLIENT_ID ? (
                      <PayPalButtons key={plan.plan_id} forceReRender={[plan.plan_id]} style={{ layout: "vertical", height: 45, shape: "pill", label: "pay" }} fundingSource={undefined} {...handlers(plan.plan_id)} />
                    ) : (
                      <div className="rounded-lg bg-secondary py-2.5 text-center text-sm text-muted-foreground">{t("paypal_missing")}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="rounded-xl border border-border bg-card p-5" data-testid="payment-history">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Receipt className="h-5 w-5 text-olive" /> {t("payment_history")}</h2>
          {history.length ? (
            <div className="divide-y divide-border">
              {history.map((h) => (
                <div key={h.order_id} data-testid={`history-${h.order_id}`} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-semibold">{t(`plan_${h.plan_id}`)} <span className="font-normal text-muted-foreground">· {h.cycle === "yearly" ? t("billing_yearly") : t("billing_monthly")}</span></p>
                    <p className="font-mono text-xs text-muted-foreground">{new Date(h.updated_at || h.created_at).toLocaleDateString()} · €{Number(h.amount).toFixed(2)}</p>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`receipt-${h.order_id}`} onClick={() => window.open(`/receipt/${h.order_id}`, "_blank")}>
                    <Receipt className="mr-1.5 h-3.5 w-3.5" /> {t("receipt")}
                  </Button>
                </div>
              ))}
            </div>
          ) : <p className="py-6 text-center text-sm text-muted-foreground">{t("no_payments")}</p>}
        </div>
      </div>
    </DashboardLayout>
  );
}
